// src/components/features/NewListing/listingContex.tsx
"use client";

import React, { createContext, useContext } from "react";
import { DraftListingData } from "./types";

export interface SharedListingStore {
  data: DraftListingData | null;
  isLoading: boolean;
  isSaving: boolean;
  isUserAgencyOwner: boolean;
  lastSaved: number | null;
  validationErrors: Record<string, string> | null; // 🚀 NEW: Store field-specific errors
  getRedirectPath: (listingId: number | string) => string;
  updateField: <K extends keyof DraftListingData>(
    field: K,
    value: DraftListingData[K],
  ) => void;
  updateMultipleFields: (updates: Partial<DraftListingData>) => void;
  saveDraft: () => Promise<void>;
  setValidationErrors: (errors: Record<string, string> | null) => void; // 🚀 NEW: Action to set errors
}

// 1. Context holds the hook and the routing function
interface ListingContextValue {
  useStoreHook: () => SharedListingStore; // We pass the hook itself!
  getRedirectPath: (listingId: number | string) => string;
}

const ListingContext = createContext<ListingContextValue | null>(null);

// 2. The Custom Hook: Components use this to get BOTH store data and routing
export function useListingContext() {
  const context = useContext(ListingContext);
  if (!context) throw new Error("Missing ListingProvider");

  // Call the Zustand hook that was provided
  const storeState = context.useStoreHook();

  // Return the combined state and redirect function
  return {
    ...storeState,
    getRedirectPath: context.getRedirectPath,
  };
}

// 3. The Provider
export function ListingProvider({
  storeHook,
  getRedirectPathAction,
  children,
}: {
  storeHook: any; // e.g., useListingStore or useEditListingStore
  getRedirectPathAction: (listingId: number | string) => string;
  children: React.ReactNode;
}) {
  return (
    <ListingContext.Provider
      value={{
        useStoreHook: storeHook,
        getRedirectPath: getRedirectPathAction,
      }}
    >
      {children}
    </ListingContext.Provider>
  );
}
